import sortBy from "lodash/sortBy";
import { Video } from "lucide-react";
import { TimelineGroupBase, TimelineItemBase } from "react-calendar-timeline";

import { ActionItemsSvg } from "@/components/pages_logged_in/program/components/ActionItemsSvg";
import { AnnouncementSvg } from "@/components/pages_logged_in/program/components/AnnouncementSvg";
import { ArticleSvg } from "@/components/pages_logged_in/program/components/ArticleSvg";
import { getComponentDatetime } from "@/components/pages_logged_in/program/components/component-utils";
import { COMPONENT_STATUS_STYLES } from "@/components/pages_logged_in/program/components/constants";
import { GroupMeetingSvg } from "@/components/pages_logged_in/program/components/GroupMeetingSvg";
import { OneOneSvg } from "@/components/pages_logged_in/program/components/OneOneSvg";
import { RoleplaySvg } from "@/components/pages_logged_in/program/components/RoleplaySvg";
import { SurveySvg } from "@/components/pages_logged_in/program/components/SurveySvg";
import { isCloseEarlyComponent } from "@/components/pages_logged_in/program/components/utils";
import { LIGHT_GRAY, SUBTEXT_COLOR } from "@/css/constants";
import { ComponentStatus, ComponentTypeEnum } from "@/graphql";
import {
  ChildComponent,
  LeadComponent,
  ParticipantComponent,
} from "@/types/programs";

type ProgramComponent = LeadComponent | ParticipantComponent | ChildComponent;

export interface TimelineItem extends TimelineItemBase<number> {
  component?: ProgramComponent;
  textColor?: string;
  background?: string;
  type?: ComponentTypeEnum;
  icon?: React.ReactNode;
  componentStatus?: ComponentStatus;
  children?: TimelineItem[];
}

export const POINT_IN_TIME_COMPONENTS: ComponentTypeEnum[] = [
  ComponentTypeEnum.Announcement,
  ComponentTypeEnum.GroupMeeting,
];

export function getGroupTimeline({
  items,
  childItems,
}: {
  items: TimelineItem[];
  childItems: TimelineItem[];
}) {
  const childGroups: TimelineGroupBase[] = [];
  const groups = items.map((item, index) => {
    const group: TimelineGroupBase = {
      id: index + 1,
      title: `Group ${index + 1}`,
      stackItems: true,
    };

    if (childItems?.some((child) => child.id === item.id)) {
      childGroups.push(group);
    }

    return group;
  });

  return [groups, childGroups];
}

function getComponentTimelineData(component: ProgramComponent, index: number) {
  const { startDate, dueDate, endDate } = getComponentDatetime({
    component: component,
  });

  const isPointInTime = component.type
    ? POINT_IN_TIME_COMPONENTS.includes(component.type)
    : false;

  const timelineItem: TimelineItem = {
    id: component.id,
    group: index + 1,
    title: component.name,
    start_time: startDate
      ? new Date(startDate).valueOf()
      : new Date().valueOf(),
    end_time: isPointInTime
      ? startDate
        ? new Date(startDate).valueOf()
        : new Date().valueOf()
      : dueDate || endDate
        ? new Date(dueDate || endDate || "").valueOf()
        : new Date().valueOf(),
    canMove: false,
    canResize: false,
    component: component,
    ...getComponentItemStyle(component),
  };
  return timelineItem;
}

export function getItemTimeline({
  components,
}: {
  components: LeadComponent[] | ParticipantComponent[];
}) {
  return sortBy(
    components.map((component, index) => {
      const timelineData = getComponentTimelineData(component, index);

      const timelineItem: TimelineItem = {
        ...timelineData,
        children: component.childComponents?.map((child) =>
          getComponentTimelineData(child, index),
        ),
      };
      return timelineItem;
    }),
    "startTime",
  );
}

export function getComponentColorsAndStatus(component: ProgramComponent) {
  const status = component?.status;
  const isCloseEarly = isCloseEarlyComponent(component);
  const isClosedEarlyWithNoResponse =
    isCloseEarly &&
    [
      ComponentStatus.DidNotFinish,
      ComponentStatus.DidNotPass,
      ComponentStatus.NoResponse,
      ComponentStatus.Missed,
    ].includes(status);

  const { color, backgroundColor } = COMPONENT_STATUS_STYLES[status] ?? {};
  const _color = isClosedEarlyWithNoResponse ? SUBTEXT_COLOR : color;
  const _bgColor = isClosedEarlyWithNoResponse ? LIGHT_GRAY : backgroundColor;

  return { _color, _bgColor, status };
}

export const getComponentItemStyle = (component: ProgramComponent) => {
  const { _color, _bgColor, status } = getComponentColorsAndStatus(component);

  let icon: React.ReactNode;
  if (component.type === ComponentTypeEnum.Announcement) {
    icon = <AnnouncementSvg size={12} fillColor={_color} />;
  }

  if (component.type === ComponentTypeEnum.ActionItem) {
    icon = <ActionItemsSvg size={14} fillColor={_color} />;
  }

  if (component.type === ComponentTypeEnum.Survey) {
    icon = <SurveySvg size={14} fillColor={_color} />;
  }

  if (component.type === ComponentTypeEnum.AiRoleplay) {
    icon = <RoleplaySvg size={14} fillColor={_color} />;
  }

  if (component.type === ComponentTypeEnum.Article) {
    icon = <ArticleSvg size={14} fillColor={_color} />;
  }

  if (component.type === ComponentTypeEnum.Video) {
    icon = <Video size={14} color={_color} />;
  }

  if (component.type === ComponentTypeEnum.GroupMeeting) {
    icon = <GroupMeetingSvg size={14} fillColor={_color} />;
  }

  if (
    [
      ComponentTypeEnum.OneOneCoachingMaster,
      ComponentTypeEnum.OneOneCoachingSession,
      ComponentTypeEnum.OneOneCoachingIntro,
    ].includes(component.type)
  ) {
    icon = <OneOneSvg size={14} fillColor={_color} />;
  }

  return {
    textColor: _color,
    background: _bgColor,
    type: component.type || ComponentTypeEnum.Announcement,
    icon: icon,
    componentStatus: status,
  };
};
